import React, { useState, useEffect } from 'react';
import { StateNodeConfig } from '@sierralabs/workflow';

import { useGetCapaStats } from '../capa/Capa.hook';
import { parseTaskStatus } from '../tasks/task.helper';
import { BarCustomLayerProps, BarLayer, ResponsiveBar } from '@nivo/bar';
import { BoxLegendSvg } from '@nivo/legends';
import { CapaStat } from '../capa/Capa.interface';
import { useFindWorkflowVersionByEntity } from '../workflow/Workflow.hooks';
import { WorkflowEntity } from '../workflow/Workflow.interface';

const CustomLegend = (props: BarCustomLayerProps<any[]>) => {
  const { legendData, innerHeight, width } = props;
  // get legend props
  const legend = legendData[0][0];
  return legend.data ? (
    <>
      <BoxLegendSvg
        key='legend-1'
        {...legend}
        data={legend.data.slice(0, 3)}
        containerHeight={legend.itemHeight}
        translateY={innerHeight + 25}
        containerWidth={width}
      />
      <BoxLegendSvg
        key='legend-2'
        {...legend}
        data={legend.data.slice(3, 6)}
        translateY={innerHeight + 50}
        containerHeight={legend.itemHeight}
        containerWidth={width}
      />
      <BoxLegendSvg
        key='legend-3'
        {...legend}
        data={legend.data.slice(6)}
        translateY={innerHeight + 75}
        containerHeight={legend.itemHeight}
        containerWidth={width}
      />
    </>
  ) : (
    <></>
  );
};

const CapaChart: React.FC = () => {
  const [stats, setStats] = useState<any[]>([]);
  const [{ data }, get] = useGetCapaStats();
  const { workflowVersion, findWorkflowVersion } =
    useFindWorkflowVersionByEntity(WorkflowEntity.Capa);

  useEffect(() => {
    findWorkflowVersion();
  }, [findWorkflowVersion]);

  useEffect(() => {
    get();
  }, [get]);

  const formattedDate = (date: string) => {
    const newDate = new Date(date);
    const year: number = newDate.getFullYear();
    const month: string = ('0' + (newDate.getMonth() + 1)).slice(-2);
    return `${year}-${month}`;
  };

  function handleSetStats(data: CapaStat[]) {
    const formattedData = data.map(stat => ({
      date: formattedDate(stat.date),
      status: parseTaskStatus(stat.status) || 'Unknown',
      value: stat.value
    }));

    const result: any = {};
    // Change Capa data structure to gather all stats that has same date value on a single object
    formattedData.map(stat => {
      const existingRecord = result[stat.date];
      if (existingRecord) {
        const updatedExistingRecord = {
          ...existingRecord,
          [stat.status]: stat.value
        };
        return (result[stat.date] = updatedExistingRecord);
      }
      result[stat.date] = {
        date: stat.date,
        [stat.status]: stat.value
      };
    });
    setStats(Object.values(result));
  }

  useEffect(() => {
    if (!data) {
      return;
    }
    handleSetStats(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const capaStatuses = Array.from(
    new Set(data?.map(stat => parseTaskStatus(stat.status)))
  ); // Remove duplicate value of status

  const getColor = (status: string) => {
    const config = workflowVersion?.config as StateNodeConfig | undefined;
    const state = config?.states?.find(state => state.name === status);
    return state?.meta?.color?.backgroundColor || '#efefef';
  };

  return (
    <div style={{ height: 300 }}>
      <ResponsiveBar
        data={stats}
        keys={capaStatuses}
        indexBy='date'
        margin={{
          top: 10,
          right: 20,
          bottom:
            capaStatuses.length < 4 ? 50 : capaStatuses.length < 7 ? 80 : 100,
          left: 20
        }}
        borderRadius={5}
        padding={0.3}
        colors={bar => {
          if (typeof bar.id === 'string') {
            return getColor(bar.id);
          }
        }}
        defs={[
          {
            id: 'dots',
            type: 'patternDots',
            background: 'inherit',
            color: '#38bcb2',
            size: 4,
            padding: 1,
            stagger: true
          },
          {
            id: 'lines',
            type: 'patternLines',
            background: 'inherit',
            color: '#eed312',
            rotation: -45,
            lineWidth: 6,
            spacing: 10
          }
        ]}
        groupMode='grouped'
        borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
        axisTop={null}
        axisRight={null}
        axisLeft={{
          format: e => {
            if (Math.floor(parseInt(`${e}`)) !== e) {
              return '';
            }
            return `${e}`;
          }
        }}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legendPosition: 'middle',
          legendOffset: 32
        }}
        labelSkipWidth={12}
        labelSkipHeight={12}
        labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
        enableGridX={true}
        layers={
          ['grid', 'axes', 'bars', 'markers', CustomLegend] as BarLayer<any>[]
        }
        legends={[
          {
            dataFrom: 'keys',
            data: capaStatuses.map((status, index) => ({
              color: getColor(status),
              value: index,
              id: status,
              label: status?.length > 16 ? status.slice(0, 16) + '...' : status
            })),
            anchor: 'bottom-left',
            direction: 'row',
            justify: false,
            itemsSpacing: 20,
            itemWidth: 110,
            itemHeight: 20,
            itemDirection: 'left-to-right',
            itemOpacity: 0.85,
            symbolSize: 20,
            symbolShape: 'circle',
            effects: [
              {
                on: 'hover',
                style: {
                  itemOpacity: 1
                }
              }
            ]
          }
        ]}
        animate={true}
      />
    </div>
  );
};

export default CapaChart;
